import React, { useState, useCallback } from "react";

interface GoogleAvatarProps {
  src: string | null | undefined;
  name?: string | null;
  email?: string | null;
  size?: number;
  className?: string;
  fallbackClassName?: string;
  fallbackContent?: React.ReactNode;
  maxRetries?: number;
}

interface GoogleAvatarState {
  trackedSrc: string | null | undefined;
  retryCount: number;
  showFallback: boolean;
  retryTimestamp: number | null;
}

/**
 * Reusable Google profile picture component with retry logic and fallback.
 * Handles cases where Google's CDN returns URLs before images are fully available.
 */
const GoogleAvatar: React.FC<GoogleAvatarProps> = ({
  src,
  name,
  email,
  size = 40,
  className = "rounded-full",
  fallbackClassName = "rounded-full bg-navy-mid",
  fallbackContent,
  maxRetries = 3,
}) => {
  // Combined state to track prop changes and retry state
  const [state, setState] = useState<GoogleAvatarState>(() => ({
    trackedSrc: src,
    retryCount: 0,
    showFallback: !src,
    retryTimestamp: null,
  }));

  // Reset state when src prop changes (setState during render is allowed in React 18)
  if (state.trackedSrc !== src) {
    setState({
      trackedSrc: src,
      retryCount: 0,
      showFallback: !src,
      retryTimestamp: null,
    });
  }

  // Compute the actual image URL from props + retry state
  const imgSrc = src
    ? state.retryTimestamp
      ? `${src}${src.includes("?") ? "&" : "?"}_retry=${state.retryTimestamp}`
      : src
    : null;

  const handleImageError = useCallback(() => {
    setState((prev) => {
      if (prev.retryCount < maxRetries) {
        // Schedule retry with exponential backoff
        const delay = 500 * Math.pow(2, prev.retryCount);
        setTimeout(() => {
          setState((s) => ({
            ...s,
            retryTimestamp: Date.now(),
            retryCount: s.retryCount + 1,
          }));
        }, delay);
        return prev;
      } else {
        // All retries exhausted, show fallback
        return { ...prev, showFallback: true };
      }
    });
  }, [maxRetries]);

  // Get initials for fallback
  const getInitials = (): string => {
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return (parts[0]?.[0] || "?").toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "?";
  };

  // Default fallback content is initials
  const defaultFallback = <span className="text-white font-semibold text-sm">{getInitials()}</span>;

  if (state.showFallback || !imgSrc) {
    return (
      <div
        className={`flex items-center justify-center ${fallbackClassName}`}
        style={{ width: size, height: size }}
      >
        {fallbackContent || defaultFallback}
      </div>
    );
  }

  return (
    <img
      src={imgSrc}
      alt={name || ""}
      className={`object-cover ${className}`}
      style={{ width: size, height: size }}
      referrerPolicy="no-referrer"
      onError={handleImageError}
    />
  );
};

export default GoogleAvatar;
